import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Eye, Edit2, Trash2, ArrowRight, Clock, CheckCircle, XCircle,
  IndianRupee, Send, User, MoreVertical, ShoppingBag,
} from 'lucide-react';
import { quotationsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ConfirmModal from '../../components/modals/ConfirmModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getApiErrorMessage } from '../../utils/apiErrors';

const QuotationsList = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [quotations, setQuotations] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [stats, setStats] = useState({ total: 0, draft: 0, sent: 0, approved: 0, rejected: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    date_from: '',
    date_to: ''
  });

  // Role-based visibility
  const [isOwner, setIsOwner] = useState(true);
  // Staff list is consumed by the StaffFilterSelect component when we
  // expose staff filtering in the header; fetched here so the filter is
  // populated the moment the header surfaces it.
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    quotation_no: '',
    customer_name: '',
    date: { min: '', max: '' },
    valid_until: { min: '', max: '' },
    status: [],
    amount: { min: '', max: '' },
    staff_member: ''
  });

  const bid = businessId || activeBusiness?.id;
  const businessPath = bid ? `/business/${bid}` : '';

  useEffect(() => {
    fetchQuotations();
  }, [filters.status, selectedStaffId]);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (selectedStaffId) params.staff_id = selectedStaffId;

      const response = await quotationsAPI.list(params);
      setQuotations(response.data?.data || response.data || []);
      if (response.data?.stats) {
        setStats(response.data.stats);
      }
      setIsOwner(response.data?.is_owner !== false);
      setStaffList(response.data?.staff_list || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load quotations'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (quotation, e) => {
    e?.stopPropagation();
    setConfirmModal({
      title: 'Delete quotation',
      description: `Delete ${quotation.doc_no}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await quotationsAPI.delete(quotation.id);
          toast.success('Quotation deleted');
          fetchQuotations();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete quotation'));
        }
      },
    });
  };

  // Server-side one-click convert. Matches the pattern used on
  // SalesOrdersPage + DeliveryChallans list -- frontend does not need
  // to prefill a create form, the backend writes the invoice directly
  // and the user lands on it. Unifies list + detail convert flows.
  const handleConvertToInvoice = (quotation, e) => {
    e?.stopPropagation();
    setConfirmModal({
      title: 'Convert to Invoice',
      description: `Convert ${quotation.doc_no} to a sales invoice?`,
      confirmLabel: 'Convert',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: ArrowRight,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await quotationsAPI.convertToInvoice(quotation.id);
          const invoice = response.data || {};
          toast.success(`Converted to invoice ${invoice.doc_no || invoice.invoice_number || ''}`);
          if (invoice.id) {
            navigate(`${businessPath}/sales/invoices/${invoice.id}`);
          } else {
            fetchQuotations();
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert quotation'));
        }
      },
    });
  };

  // Mirror convert-to-sales-order on the list (previously only on the
  // detail page). Matches the industry-standard "one click per transition"
  // pattern used by sales_orders + delivery_challans.
  const handleConvertToSalesOrder = (quotation, e) => {
    e?.stopPropagation();
    setConfirmModal({
      title: 'Convert to Sales Order',
      description: `Convert ${quotation.doc_no} to a sales order?`,
      confirmLabel: 'Convert',
      confirmClass: 'bg-purple-600 hover:bg-purple-700',
      icon: ShoppingBag,
      iconClass: 'text-purple-600',
      iconBgClass: 'bg-purple-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await quotationsAPI.convertToSalesOrder(quotation.id);
          const order = response.data?.sales_order || response.data || {};
          toast.success(`Converted to sales order ${order.order_number || ''}`);
          if (order.id) {
            navigate(`${businessPath}/sales-orders/${order.id}`);
          } else {
            fetchQuotations();
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert quotation'));
        }
      },
    });
  };

  // Badge map -- backend lifecycle is DRAFT -> SENT -> APPROVED/REJECTED,
  // plus CONVERTED (set by the convert endpoints) and EXPIRED (set by a
  // future scheduled job; shown here so rows don't fall through to Draft).
  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
      SENT: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
      APPROVED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Approved' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
      CANCELLED: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Cancelled' },
      CONVERTED: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Converted' },
      EXPIRED: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Expired' },
    };
    return badges[status] || badges.DRAFT;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const handleView = (quotation) => {
    navigate(`${businessPath}/sales/quotations/${quotation.id}`);
  };

  // Apply all filters
  const filteredQuotations = quotations.filter(q => {
    const matchesSearch = !filters.search ||
      (q.doc_no || q.quotation_no)?.toLowerCase().includes(filters.search.toLowerCase()) ||
      q.party?.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      q.party_name?.toLowerCase().includes(filters.search.toLowerCase());

    const matchesQuotNo = !columnFilters.quotation_no ||
      (q.doc_no || q.quotation_no)?.toLowerCase().includes(columnFilters.quotation_no.toLowerCase());

    const customerName = q.party?.name || q.party_name || '';
    const matchesCustomer = !columnFilters.customer_name ||
      customerName.toLowerCase().includes(columnFilters.customer_name.toLowerCase());

    const qDate = q.doc_date || q.quotation_date;
    const matchesDate = (!columnFilters.date?.min || new Date(qDate) >= new Date(columnFilters.date.min)) &&
      (!columnFilters.date?.max || new Date(qDate) <= new Date(columnFilters.date.max));

    const validDate = q.meta?.valid_until || q.valid_until;
    const matchesValidUntil = (!columnFilters.valid_until?.min || new Date(validDate) >= new Date(columnFilters.valid_until.min)) &&
      (!columnFilters.valid_until?.max || new Date(validDate) <= new Date(columnFilters.valid_until.max));

    const matchesStatus = columnFilters.status.length === 0 || columnFilters.status.includes(q.status);

    const amount = q.totals?.grand_total || q.grand_total || 0;
    const matchesAmount = (!columnFilters.amount?.min || amount >= parseFloat(columnFilters.amount.min)) &&
      (!columnFilters.amount?.max || amount <= parseFloat(columnFilters.amount.max));

    const matchesStaff = !columnFilters.staff_member ||
      (q.created_by_staff_name || '').toLowerCase().includes(columnFilters.staff_member.toLowerCase());

    return matchesSearch && matchesQuotNo && matchesCustomer && matchesDate && matchesValidUntil && matchesStatus && matchesAmount && matchesStaff;
  });

  return (
    <>
    <div className="h-full flex flex-col" data-testid="quotations-list-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Quotations"
        stats={[
          { icon: FileText, label: 'Total', value: stats.total, color: 'slate' },
          { icon: Clock, label: 'Draft', value: stats.draft, color: 'amber' },
          { icon: Send, label: 'Sent', value: stats.sent, color: 'blue' },
          { icon: CheckCircle, label: 'Approved', value: stats.approved, color: 'emerald' },
          { icon: IndianRupee, label: 'Total Value', value: formatCompactCurrency(stats.total_value), color: 'emerald' },
        ]}
        ctaLabel="New Quotation"
        onCtaClick={() => navigate(`${businessPath}/sales/quotations/new`)}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading quotations...</p>
              </div>
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No quotations found</p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">Create your first quotation to get started</p>
              <button
                onClick={() => navigate(`${businessPath}/sales/quotations/new`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-[13px] font-medium"
              >
                Create Quotation
              </button>
            </div>
          ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Single table: sticky thead eliminates two-table column misalignment */}
                <div className="flex-1 overflow-y-auto">
                  <table className="tl-table w-full">
                    <thead className="sticky top-0 z-10 bg-white">
                      <tr>
                        <th className="tl-th tl-th-teal" style={{ width: isOwner ? '12%' : '14%' }}>
                          <div className="flex items-center gap-1">
                            Quotation #
                            <ColumnFilter type="text" value={columnFilters.quotation_no} onChange={(val) => setColumnFilters({...columnFilters, quotation_no: val})} placeholder="Filter..." />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: isOwner ? '18%' : '22%' }}>
                          <div className="flex items-center gap-1">
                            Customer
                            <ColumnFilter type="text" value={columnFilters.customer_name} onChange={(val) => setColumnFilters({...columnFilters, customer_name: val})} placeholder="Filter..." />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '11%' }}>
                          <div className="flex items-center gap-1">
                            Date
                            <ColumnFilter type="date" value={columnFilters.date} onChange={(val) => setColumnFilters({...columnFilters, date: val})} />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '11%' }}>
                          <div className="flex items-center gap-1">
                            Valid Until
                            <ColumnFilter type="date" value={columnFilters.valid_until} onChange={(val) => setColumnFilters({...columnFilters, valid_until: val})} />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                          <div className="flex items-center gap-1">
                            Status
                            <ColumnFilter type="status" value={columnFilters.status} onChange={(val) => setColumnFilters({...columnFilters, status: val})} options={[
                              { value: 'DRAFT', label: 'Draft' },
                              { value: 'SENT', label: 'Sent' },
                              { value: 'APPROVED', label: 'Approved' },
                              { value: 'REJECTED', label: 'Rejected' },
                              { value: 'CONVERTED', label: 'Converted' },
                              { value: 'EXPIRED', label: 'Expired' },
                            ]} />
                          </div>
                        </th>
                        {/* Staff Member Column - Owner Only */}
                        {isOwner && (
                          <th className="tl-th tl-th-teal" style={{ width: '13%' }}>
                            <div className="flex items-center gap-1">
                              <User className="w-3.5 h-3.5" />
                              Staff
                              <ColumnFilter
                                type="text"
                                value={columnFilters.staff_member}
                                onChange={(val) => setColumnFilters({...columnFilters, staff_member: val})}
                                placeholder="Filter staff..."
                              />
                            </div>
                          </th>
                        )}
                        <th className="tl-th tl-th-teal tl-th-right" style={{ width: '13%' }}>
                          <div className="flex items-center justify-end gap-1">
                            Amount
                            <ColumnFilter type="number" value={columnFilters.amount} onChange={(val) => setColumnFilters({...columnFilters, amount: val})} />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '60px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="animate-stagger">
                      {filteredQuotations.map((quotation, index) => {
                        const statusBadge = getStatusBadge(quotation.status);
                        const isDraft = quotation.status === 'DRAFT';
                        const isApproved = quotation.status === 'APPROVED';
                        const isConverted = quotation.status === 'CONVERTED';
                        const canDelete = !isConverted && quotation.status !== 'REJECTED';
                        return (
                        <tr
                          key={quotation.id}
                          className={`tl-tr tl-tr-clickable tl-tr-hover ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          onClick={() => handleView(quotation)}
                          style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                        >
                          <td className="tl-td" style={{ width: isOwner ? '12%' : '14%' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-medium text-teal-700">
                                  {(quotation.doc_no || quotation.quotation_no)?.slice(-2) || 'QT'}
                                </span>
                              </div>
                              <span className="tl-cell-primary">{quotation.doc_no || quotation.quotation_no}</span>
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: isOwner ? '18%' : '22%' }}>
                            {quotation.party?.name || quotation.party_name || '—'}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '11%' }}>
                            {formatDate(quotation.doc_date || quotation.quotation_date)}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '11%' }}>
                            {formatDate(quotation.meta?.valid_until || quotation.valid_until)}
                          </td>
                          <td className="tl-td" style={{ width: '10%' }}>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </td>
                          {isOwner && (
                            <td className="tl-td" style={{ width: '13%' }}>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-3 h-3 text-slate-500" />
                                </div>
                                <span className="text-[12px] text-slate-600 truncate">
                                  {quotation.created_by_staff_name || 'Owner'}
                                </span>
                              </div>
                            </td>
                          )}
                          <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '13%' }}>
                            {formatCurrency(quotation.totals?.grand_total || quotation.grand_total)}
                          </td>
                          <td className="tl-td" style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                                  title="Actions"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => handleView(quotation)}>
                                  <Eye className="w-4 h-4 mr-2 text-slate-500" />
                                  View
                                </DropdownMenuItem>
                                {isDraft && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); navigate(`${businessPath}/sales/quotations/${quotation.id}/edit`); }}
                                  >
                                    <Edit2 className="w-4 h-4 mr-2 text-slate-500" />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                {isApproved && (
                                  <>
                                    <DropdownMenuItem onClick={(e) => handleConvertToSalesOrder(quotation, e)}>
                                      <ShoppingBag className="w-4 h-4 mr-2 text-purple-600" />
                                      Convert to Sales Order
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => handleConvertToInvoice(quotation, e)}>
                                      <ArrowRight className="w-4 h-4 mr-2 text-blue-600" />
                                      Convert to Invoice
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {canDelete && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={(e) => handleDelete(quotation, e)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* TABLE FOOTER */}
                <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                  <p>Showing <span className="font-medium text-slate-700">{filteredQuotations.length}</span> quotations</p>
                  <p>Total Value: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.total_value)}</span></p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconClass={confirmModal?.iconClass}
        iconBgClass={confirmModal?.iconBgClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </>
  );
};

export default QuotationsList;
